/**
 * 场景对话（虚拟人老师）
 * ============================================================
 * 对应 Midoo 的「对话」功能，含**两期**：
 *   第一期：引导式对话（提示栏 + 照读）
 *   第二期：水平够高时解锁**自由回答**；跑题时**分级拉回**
 *
 * ── 视觉：与全站一致的浅色主题 ──────────────────────────────
 *   这一页最初按深色写，与全站（首页/翻译/收藏都是浅色）不一致，
 *   已改回浅色。深色只应出现在**沉浸式课堂**里，对话页不是。
 *
 * ── 布局：底部输入区不能被导航栏挡住 ────────────────────────
 *   导航是 fixed 覆盖在视口底部。所以这一页的根容器用
 *   h-screen + paddingBottom（导航高度），让 flex 列的底部
 *   停在导航上方，输入区不会被压住。
 *
 * ── 针对实测延迟的设计（实测：LLM 4.4s + TTS 0.9s ≈ 5.4s/轮）──
 *   1. 老师台词一到就立刻播放，不等其他数据
 *   2. **并行预合成**提示答案的音频 → 点提示秒开
 *   3. 明确的"老师在思考…3s"状态（等待不可怕，不知道在等才可怕）
 *
 * ── 诚实性 ──────────────────────────────────────────────────
 *   · 对话由 AI 生成，**未经母语者验证**，界面已标注
 *   · 逐字比对**不是发音评分**
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { apiUrl } from '../lib/api';
import { getDeviceId } from '../lib/deviceUtils';
import { getSelectedDialectId, fetchDialect } from '../lib/dialects';
import { getSelectedTutorId, pickTutor, fetchTutors } from '../lib/tutorUtils';
import { resolveTutorForDialect, loadTutorClips, resolveClipUrl } from '../lib/tutorClip';
import { getTeacherAsset } from '../lib/teacherAssets';
import { recognizeViaRelay } from '../lib/voiceRelay';
import { useRecorder } from '../hooks/useRecorder';
import BottomNav from '../components/BottomNav';
import { putAudio, getAudio } from '../lib/savedPhrases';

/** 底部导航高度（含安全区）。输入区据此上移，避免被 fixed 的导航压住。 */
const NAV_HEIGHT = 'calc(76px + env(safe-area-inset-bottom))';

export default function ConversationPage() {
  const navigate = useNavigate();

  const [dialect, setDialect] = useState(null);
  const [tutor, setTutor] = useState(null);
  const [state, setState] = useState(null);
  const [turn, setTurn] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | interest | scenario | vocab | chat | wrapup | error

  const [thinking, setThinking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  /*
   * 流式过程中**已经拿到、但整轮还没结束**的老师台词。
   *
   * 非流式时学生要等 5 秒才看到第一个字；流式后模型吐出 teacherLine
   * 就能显示并开口，提示栏晚一两秒到没关系。
   * 非空表示"台词已到、还在等提示"，界面据此判断该显示什么。
   */
  const [streamLine, setStreamLine] = useState(null);
  const [error, setError] = useState(null);
  // 方言开放档位：full / partial / blocked（blocked 时不进对话）
  const [support, setSupport] = useState(null);
  const [blocked, setBlocked] = useState(null);

  const [speaking, setSpeaking] = useState(false);
  /*
   * 当前要播放的口型片段 URL（非空时半屏虚拟人播这个片段，而非 idle）。
   * 只有开场白这类**预生成**的固定台词才有片段；实时生成的对话台词没有。
   * 片段自带音轨，播完自动清空、切回 idle。
   */
  const [clipUrl, setClipUrl] = useState(null);

  const [hintOpen, setHintOpen] = useState(false);
  const [usedHintThisTurn, setUsedHintThisTurn] = useState(false);
  const [chosenHint, setChosenHint] = useState(null);
  const [hintAudio, setHintAudio] = useState(() => new Map());
  const [prefetching, setPrefetching] = useState(false);

  const [heard, setHeard] = useState(null);
  const [checking, setChecking] = useState(false);
  const [draft, setDraft] = useState(''); // 兴趣阶段的文字输入
  /*
   * 兴趣阶段默认**用说的**。
   * 老师的开场就是"想先了解了解你"，这时学生对着麦克风说一句"我喜欢吃面"
   * 是最自然的事；让他切到键盘打字，等于在学口语的入口处先要求写字。
   * 但仍然保留打字入口 —— 在地铁上、旁边有人时确实不方便说话。
   */
  const [textMode, setTextMode] = useState(false);

  const audioRef = useRef(null);
  const urlRef = useRef(null);
  /*
   * 当前老师的 id（ref 而不是 state）。
   *
   * speak() 是 useCallback([])，闭包里拿到的 tutor 永远是挂载时的 null，
   * 于是讲解/拉回话术这些**普通话**台词会退回默认音色（女声）——
   * 男老师的对话里就出现「男声说方言、女声讲普通话」。
   * 课堂页也踩过同一个坑，所以这里直接用 ref。
   */
  const tutorRef = useRef(null);
  /** 本轮已经播出去的台词（流式下用于判断 done 时要不要补播） */
  const spokenLineRef = useRef('');
  const recorder = useRecorder();

  /* ============================================================
   * 初始化
   * ============================================================ */
  useEffect(() => {
    (async () => {
      try {
        const d = await fetchDialect(getSelectedDialectId()).catch(() => null);
        setDialect(d);

        const persona = await fetchTutors()
          .then((r) => pickTutor(r.tutors, getSelectedTutorId()))
          .catch(() => null);

        /*
         * 老师形象/音色由**方言**决定，不由用户选的人设决定 —— 与课堂页同一套规则。
         *
         * 原因：陕西(Marcus)/南京(Li)/闽南(Roy) 只有男声，粤语/四川/上海是女声。
         * 如果这里用用户选的人设（默认阿清），陕西话课会变成
         * 「阿清的名字 + 男声」，而刚才修的"讲解音色跟老师性别走"也会在这里断掉。
         */
        const assetId = await resolveTutorForDialect(
          getSelectedDialectId(),
          persona?.id || 'aqing'
        );
        const asset = await getTeacherAsset(assetId).catch(() => null);
        const nextTutor = {
          ...(persona || {}),
          id: assetId,
          name: asset?.name || persona?.name || assetId,
          portrait: asset?.portrait || null,
          // 半屏虚拟人用的循环素材（无口型驱动）
          idleVideo: asset?.idleVideo || null,
          idlePoster: asset?.idlePoster || asset?.portrait || null,
        };
        tutorRef.current = nextTutor;
        setTutor(nextTutor);

        const res = await api.post('/api/conversation/start', {
          dialectId: getSelectedDialectId(),
          tutorId: assetId,
        });
        const data = res.data?.data;
        setSupport(data.support || null);
        setState(data.state);
        setTurn(data.turn);
        setPhase(data.state.stage);
        // 开场白优先对口型片段（模板化台词有预生成片段），否则回退 TTS
        speakOpening(data.turn.speak.text, data.turn.speak.language);
      } catch (e) {
        const resp = e.response?.data;
        // 方言未开放：不是错误，是**能力限制** —— 要给用户解释和替代方案，
        // 不能只甩一句"失败"让人猜。
        if (resp?.code === 'DIALECT_NOT_SUPPORTED') {
          setBlocked({ reason: resp.error, support: resp.support });
          setPhase('blocked');
          return;
        }
        setError(resp?.error || e.message || '无法开始对话');
        setPhase('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      try {
        audioRef.current?.pause();
      } catch {
        /* 忽略 */
      }
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    []
  );

  useEffect(() => {
    if (!thinking) return undefined;
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [thinking]);

  /*
   * 第一次进入对话阶段时，**自动展开一次提示栏**。
   *
   * 原因：新用户看到老师说话了、也知道该回答，但不知道下面有个折叠的
   * "看看提示"，于是对着麦克风干等 —— 这是最常见的第一次卡住的地方。
   *
   * ⚠️ 自动展开**不能**算作"用了提示"（不调用 toggleHint / setUsedHintThisTurn）。
   *    它只是让功能可见；学生实际有没有照念，由他是否点了具体那条决定。
   *    否则每个新用户一进来就被扣一轮水平分。
   */
  const hintAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (phase !== 'chat' || hintAutoOpenedRef.current) return;
    hintAutoOpenedRef.current = true;
    setHintOpen(true);
  }, [phase]);

  /* ============================================================
   * 播放
   * ============================================================ */

  /**
   * @param {number} [rate=1] 播放速度。0.7 = 慢速重放
   *
   * 慢放走**前端 playbackRate**，不重新调 TTS：
   *   · 零等待 —— 重新合成要 1.3 秒，而学生正卡在听不懂的那一句上
   *   · 不变调 —— `preservesPitch` 默认 true，降速不会变成"低沉怪声"
   *   · 省额度 —— 不消耗一次合成
   * 方言语速偏快时这是最直接的补救办法。
   */
  const speak = useCallback(async (text, language, rate = 1) => {
    if (!text) return;
    audioRef.current?.pause();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    setSpeaking(true);
    try {
      const resp = await api.post(
        '/api/tts',
        {
          text,
          language: language === 'dialect' ? 'cantonese' : language || 'mandarin',
          dialect: language === 'dialect' ? getSelectedDialectId() : undefined,
          // 普通话台词要带 tutor：讲解音色必须与老师性别一致（后端读 mandarinVoice）
          tutor: tutorRef.current?.id,
        },
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(resp.data);
      urlRef.current = url;
      if (!audioRef.current) audioRef.current = new Audio();
      const a = audioRef.current;
      a.src = url;
      a.playbackRate = rate;
      a.preservesPitch = true; // 显式声明：慢放但不降调
      a.onended = () => setSpeaking(false);
      a.onerror = () => setSpeaking(false);
      await a.play().catch(() => setSpeaking(false));
    } catch {
      setSpeaking(false);
    }
  }, []);

  /*
   * 开场白专用播放：优先对口型片段，没有才退回 TTS。
   *
   * 开场白是**模板化固定台词**（config/conversation-opening.js），预生成了
   * 口型片段。这里用 loadTutorClips + resolveClipUrl 找片段：
   *   · 命中 → 设 clipUrl，半屏虚拟人切到片段播放（片段自带音轨，天然对口型）
   *   · 未命中（片段还没生成/方言没配）→ 退回 speak()，走 TTS + idle
   * 两者都对 speaking 状态置 true，声波条照常显示。
   */
  const speakOpening = useCallback(
    async (text, language) => {
      if (!text) return;
      const dialectId = getSelectedDialectId();
      const tutorId = tutorRef.current?.id;
      try {
        const clipsInfo = await loadTutorClips(tutorId, dialectId);
        const url = resolveClipUrl(text, clipsInfo);
        if (url) {
          setSpeaking(true);
          setClipUrl(url);
          return;
        }
      } catch {
        /* 清单拿不到就退回 TTS，不影响开场 */
      }
      await speak(text, language);
    },
    [speak]
  );

  /** 预合成提示音频 —— 对抗 TTS 延迟的关键 */
  const prefetchHints = useCallback(async (hints) => {
    if (!hints?.length) return;
    setPrefetching(true);
    const dialectId = getSelectedDialectId();
    await Promise.all(
      hints.map(async (h) => {
        const key = `${dialectId}::hint::${h.text}`;
        try {
          const cached = await getAudio(key);
          if (cached) {
            setHintAudio((prev) => new Map(prev).set(h.text, cached));
            return;
          }
          const resp = await api.post(
            '/api/tts',
            { text: h.text, language: 'cantonese', dialect: dialectId },
            { responseType: 'blob' }
          );
          setHintAudio((prev) => new Map(prev).set(h.text, resp.data));
          putAudio(key, resp.data).catch(() => {});
        } catch {
          /* 预合成失败不影响主流程 */
        }
      })
    );
    setPrefetching(false);
  }, []);

  /* ============================================================
   * 走一轮（流式）
   * ============================================================ */
  const advance = useCallback(
    async ({ userText } = {}) => {
      if (!state || thinking || streamLine) return;
      setThinking(true);
      setError(null);
      setStreamLine(null);
      spokenLineRef.current = '';

      const payload = {
        state,
        userText: userText || '',
        usedHint: usedHintThisTurn || !!chosenHint,
        matchedHint: chosenHint?.text || '',
        /*
         * 上一轮给过的候选答案 —— 学生**没点提示**时，服务端拿它当参考答案，
         * 判断"他是不是用自己的话说对了"。
         *
         * 不传的话：学生不点提示 → 没有比对对象 → 吻合度恒为 0 →
         * 水平判定永远涨不上去，「自由回答」这个功能永远不会解锁。
         */
        expectedHints: (turn?.hints || []).map((h) => h.text).filter(Boolean),
      };

      try {
        /*
         * SSE 用原生 fetch（axios 拿不到可读流），所以地址**必须自己拼**。
         * 生产环境 API 在另一个域名，裸写 '/api/...' 会请求到静态站点，
         * 拿到 200 + index.html，然后 SSE 解析不出任何事件 —— 表现为
         * "老师一直不说话"，而不是报错。
         */
        const resp = await fetch(apiUrl('/api/conversation/turn/stream'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            /*
             * 这里用原生 fetch（axios 不支持 SSE 流式读取），
             * 所以 axios 的请求拦截器不会自动加设备标识 —— 必须手动带。
             * 漏了它，授权启用后服务端 requireQuota 会返回 401 NO_DEVICE。
             * 与 api.js 里拦截器用同一个 getDeviceId()，别另造 ID。
             */
            'x-device-key': getDeviceId(),
            'x-user-id': getDeviceId(),
          },
          body: JSON.stringify(payload),
        });
        if (!resp.ok) {
          const t = await resp.text().catch(() => '');
          throw new Error(`对话服务返回 ${resp.status}${t ? `：${t.slice(0, 80)}` : ''}`);
        }
        if (!resp.body) throw new Error('当前浏览器不支持流式响应');

        const reader = resp.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        let done = null;

        for (;;) {
          const { value, done: fin } = await reader.read();
          if (fin) break;
          buf += dec.decode(value, { stream: true });

          // SSE 事件以空行分隔；跨 chunk 的半截留在 buf 里
          let idx;
          while ((idx = buf.indexOf('\n\n')) >= 0) {
            const block = buf.slice(0, idx);
            buf = buf.slice(idx + 2);

            const ev = /^event:\s*(.+)$/m.exec(block);
            const dl = /^data:\s*(.+)$/m.exec(block);
            if (!ev || !dl) continue;

            let data;
            try {
              data = JSON.parse(dl[1]);
            } catch {
              continue;
            }

            const name = ev[1].trim();
            if (name === 'line') {
              /*
               * 台词到了。这是整个流式改造的意义所在 ——
               * 非流式要等 5 秒才有第一个字，这里 1~2 秒就能开口。
               * 提示栏、话题进度这些晚一点到完全没关系。
               */
              const text = String(data.text || '').trim();
              if (!text) continue;
              setStreamLine(text);
              spokenLineRef.current = text;
              speak(text, 'dialect');
            } else if (name === 'done') {
              done = data;
            } else if (name === 'error') {
              throw new Error(data.error || '对话失败');
            }
          }
        }

        if (!done) throw new Error('对话中断了，请再试一次');

        setState(done.state);
        setTurn(done.turn);
        setPhase(done.state.stage);
        setUsedHintThisTurn(false);
        setChosenHint(null);
        setHeard(null);
        setHintOpen(false);
        recorder.resetRecording();

        /*
         * 正常情况台词刚才已经播过，不重复。
         * 只有一种例外：这一轮被判定跑题，服务端用"拉回话术"替换了台词
         * （语言也从方言变成普通话），这时要补播真正的那句。
         */
        const finalText = String(done.turn?.speak?.text || '').trim();
        if (finalText && finalText !== spokenLineRef.current) {
          speak(finalText, done.turn.speak.language);
        }
        if (done.turn?.hints?.length) prefetchHints(done.turn.hints);
      } catch (e) {
        setError(e.response?.data?.error || e.message || '对话失败');
      } finally {
        setThinking(false);
        setStreamLine(null);
      }
    },
    [state, thinking, streamLine, usedHintThisTurn, chosenHint, turn, speak, prefetchHints, recorder]
  );

  /* ============================================================
   * 录音 → 识别 → 提交
   * ============================================================ */
  const submitSpeech = useCallback(async () => {
    const file = await recorder.getAudioFile();
    if (!file) {
      setError('没有取到录音，请重录');
      return;
    }

    /*
     * 过短录音直接拦掉。
     *
     * 误触、环境噪音、按一下就走 —— 这些都会产生 0.2 秒的音频，
     * 送进去识别通常返回一两个字（或空），然后被当成"学生说了话"，
     * 白烧一次 LLM 调用（实测 5 秒），还会打乱水平判定。
     * 与其识别完再说"没听清"，不如在门口拦住。
     */
    if (typeof recorder.duration === 'number' && recorder.duration < 0.6) {
      setError('录音太短了。请说完一整句再点停止。');
      recorder.resetRecording();
      return;
    }

    setChecking(true);
    setError(null);
    try {
      /*
       * 兴趣采集阶段老师用**普通话**问"你平时喜欢做什么"，学生也必然用普通话答。
       * 这一段不能走方言识别：识别歪了，后面生成的整个场景都跟着歪，
       * 而学生只会觉得"这老师听不懂人话"。
       */
      const asrDialect = phase === 'interest' ? 'mandarin' : getSelectedDialectId();

      /*
       * 先试中国侧中继（手机→上海→杭州，实测 0.5~1.3s），
       * 失败再走主服务（手机→美东→杭州，实测 5.7~8.5s）。
       * 中继不可用时这个调用会安静返回 null，行为与改动前完全一致。
       */
      const relayed = await recognizeViaRelay(file, asrDialect);

      let text = relayed?.text || '';
      if (!relayed) {
        const fd = new FormData();
        fd.append('audio', file);
        fd.append('dialect', asrDialect);
        const res = await api.post('/api/recognize', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        text = String(res.data?.data?.text || '').trim();
      }

      /*
       * 识别为空 = 没听清（环境吵、太小、或说的是普通话）。
       * 不能拿空文本往下走 —— 那会生成一个"老师回应空气"的回合，
       * 学生还不知道为什么老师答非所问。
       */
      if (!text) {
        setError('没听清你说什么。靠近一点、大声一点再说一次？');
        recorder.resetRecording();
        return;
      }

      setHeard(text);
      await advance({ userText: text });
    } catch (e) {
      setError(e.response?.data?.error || e.message || '识别失败');
    } finally {
      setChecking(false);
    }
  }, [recorder, advance, phase]);

  /* ============================================================
   * 提示栏
   * ============================================================ */
  const toggleHint = () => {
    const next = !hintOpen;
    setHintOpen(next);
    // 打开过就算"用了提示" —— 水平判定的依据，不能漏记
    if (next) setUsedHintThisTurn(true);
  };

  const playHint = (h) => {
    setChosenHint(h);
    if (hintOpen) setUsedHintThisTurn(true);
    audioRef.current?.pause();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    const blob = hintAudio.get(h.text);
    if (blob) {
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      if (!audioRef.current) audioRef.current = new Audio();
      const a = audioRef.current;
      a.src = url;
      // 提示音频按正常速度播；不重置的话会沿用上一次"慢一点"留下的 0.7 倍速
      a.playbackRate = 1;
      a.preservesPitch = true;
      a.onended = () => setSpeaking(false);
      setSpeaking(true);
      a.play().catch(() => {
        setSpeaking(false);
        speak(h.text, 'dialect');
      });
    } else {
      speak(h.text, 'dialect');
    }
  };

  /* ============================================================
   * 渲染
   * ============================================================ */

  if (phase === 'loading') {
    return (
      <Shell>
        <div className="flex flex-1 items-center justify-center">
          <span className="material-icons animate-spin text-3xl text-zinc-300">refresh</span>
        </div>
        <BottomNav />
      </Shell>
    );
  }

  /* 方言未开放：说清原因 + 给替代方案，不是一句"失败" */
  if (phase === 'blocked') {
    return (
      <Shell>
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <span className="material-icons text-4xl text-amber-500">info</span>
          <p className="mt-3 text-sm font-bold text-zinc-700 dark:text-zinc-200">
            {dialect?.displayName || '这个方言'}暂时不能进入对话
          </p>
          <p className="mt-2 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
            {blocked?.reason}
          </p>
          <p className="mt-2 text-[11px] leading-relaxed text-zinc-400">
            {blocked?.support?.alternative}
          </p>
          <div className="mt-6 flex gap-2">
            <Link
              to="/dialects"
              className="rounded-full bg-teal-500 px-4 py-2 text-xs font-bold text-white"
            >
              换个方言
            </Link>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-full bg-zinc-100 px-4 py-2 text-xs font-bold text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300"
            >
              返回首页
            </button>
          </div>
        </div>
        <BottomNav />
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell>
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
          <span className="material-icons text-4xl text-zinc-300">error_outline</span>
          <p className="mt-3 text-sm text-zinc-500">{error}</p>
          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="rounded-full bg-zinc-100 px-4 py-2 text-xs font-bold text-zinc-600"
            >
              返回首页
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-full bg-teal-500 px-4 py-2 text-xs font-bold text-white"
            >
              重试
            </button>
          </div>
        </div>
        <BottomNav />
      </Shell>
    );
  }

  const lv = state?.level || {};
  const isChat = phase === 'chat';
  const isWrapup = phase === 'wrapup' || state?.ended;
  const isInterest = phase === 'interest';
  /*
   * 还在等这一轮的结果。
   *
   * `streamLine` 非空 = 台词已经到了、还在等提示，也算"忙"——
   * 否则输入区会提前放出来，学生可能在提示还没到时就开口。
   */
  const busy = thinking || !!streamLine;
  /** 老师这一句要显示什么：流式先到的那句优先，其次是已定的台词 */
  const teacherText = streamLine || turn?.speak?.text || '';
  const teacherLang = streamLine ? 'dialect' : turn?.speak?.language;

  /**
   * 录音区 —— 对话阶段与兴趣采集阶段共用，只有提示文案不同。
   *
   * 抽出来是因为两处行为必须完全一致（同一套"过短拦截 / 识别为空拦截 /
   * 提交后清空"的逻辑在 submitSpeech 里，UI 不能各写一套）。
   */
  const speechBar = (hint) => (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => {
          if (recorder.isRecording) {
            recorder.stopRecording();
          } else {
            recorder.resetRecording();
            setHeard(null);
            recorder.startRecording();
          }
        }}
        className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-white transition active:scale-95 ${
          recorder.isRecording ? 'bg-rose-500' : 'bg-teal-500'
        }`}
        aria-label={recorder.isRecording ? '停止' : '开始说'}
      >
        <span className="material-icons !text-2xl">
          {recorder.isRecording ? 'stop' : 'mic'}
        </span>
      </button>
      <div className="min-w-0 flex-1">
        {!recorder.audioUrl ? (
          <p className="text-[11px] leading-relaxed text-zinc-500">
            {recorder.isRecording ? `录音中 ${recorder.duration}s，说完点方块` : hint}
          </p>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={submitSpeech}
              disabled={checking}
              className="flex-1 rounded-xl bg-teal-500 py-2.5 text-xs font-bold text-white disabled:opacity-50"
            >
              {checking ? '识别中…' : '提交'}
            </button>
            <button
              type="button"
              onClick={() => recorder.resetRecording()}
              disabled={checking}
              className="rounded-xl bg-zinc-100 px-3 py-2.5 text-xs font-bold text-zinc-600 disabled:opacity-50 dark:bg-zinc-700 dark:text-zinc-300"
            >
              重录
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Shell>
      {/*
        ── 半屏虚拟人 ────────────────────────────────────────
        参考 Midoo：上方固定一块人物区，下方才是对话。这样人物"在场"，
        又不会像全屏那样把对话内容挤掉。

        这里循环播放的是**基准视频压出来的 idle 素材**（无音轨）：
        人物会眨眼、微微动头 —— 这些是拍摄时就有的真实动作，不是拿图片晃。

        ⚠️ 但它**不对口型**。对话是 LLM 实时生成的，没有预生成片段，
           硬做口型只会变成"假唱"。宁可不对口，也不做错的口型
           （这是本项目一贯的红线）。
      */}
      <header className="relative shrink-0 overflow-hidden bg-zinc-900" style={{ height: '32vh', minHeight: 190 }}>
        {clipUrl ? (
          /*
           * 开场白命中口型片段：播这个片段（自带音轨，对口型）。
           * 播完自动清空 clipUrl、切回 idle。不 mute、不 loop。
           */
          <video
            key={clipUrl}
            src={clipUrl}
            poster={tutor?.idlePoster || tutor?.portrait || undefined}
            autoPlay
            playsInline
            preload="auto"
            onEnded={() => {
              setClipUrl(null);
              setSpeaking(false);
            }}
            onError={() => {
              // 片段加载失败（404/清单过期）→ 回退 idle，说话状态交给 TTS 兜底
              setClipUrl(null);
              setSpeaking(false);
            }}
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
        ) : tutor?.idleVideo ? (
          <video
            src={tutor.idleVideo}
            poster={tutor.idlePoster || undefined}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
        ) : tutor?.portrait ? (
          <img
            src={tutor.portrait}
            alt={tutor.name || '老师'}
            className="absolute inset-0 h-full w-full object-cover object-top"
          />
        ) : null}

        {/* 上下两段渐变：保证按钮/文字在任何画面上都读得清 */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/65 to-transparent" />

        {/* 顶部：返回 + 场景标题 */}
        <div className="relative flex items-start gap-2.5 px-4 pt-10">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/25 text-white backdrop-blur active:scale-95"
            aria-label="结束对话"
          >
            <span className="material-icons !text-base">close</span>
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-white drop-shadow">
              {state?.scenario?.title || '聊聊你想学什么'}
            </p>
            <p className="text-[10px] text-white/80">
              {dialect?.displayName}
              {state?.turnCount ? ` · 第 ${state.turnCount} 轮` : ''}
            </p>
          </div>
          {isChat && (
            <span
              className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold backdrop-blur ${
                lv.freeMode ? 'bg-white/35 text-white' : 'bg-white/15 text-white/80'
              }`}
            >
              {lv.freeMode ? '自由回答' : '引导模式'}
            </span>
          )}
        </div>

        {/* 底部：老师名字 + 说话状态（声波条） */}
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 px-4 pb-2.5">
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              speaking ? 'animate-pulse bg-emerald-400' : 'bg-white/50'
            }`}
          />
          <span className="text-[11px] font-bold text-white drop-shadow">
            {tutor?.name || '老师'}
          </span>
          {speaking && (
            <span className="flex items-end gap-[2px]">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-[2px] animate-pulse rounded-full bg-emerald-400"
                  style={{ height: 6 + i * 4, animationDelay: `${i * 120}ms` }}
                />
              ))}
            </span>
          )}
        </div>
      </header>

      {/* 内容区：可滚动 */}
      <main className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {/*
          部分可用方言的警示。
          用户有权知道这个方言的质量还没完全验证 —— 尤其对话是自由生成的，
          比固定课程更容易出错。
        */}
        {support?.level === 'partial' && (
          <section className="rounded-2xl bg-amber-50 p-3 dark:bg-amber-900/30">
            <p className="text-[10px] leading-relaxed text-amber-800 dark:text-amber-200">
              <span className="font-bold">这个方言还在试用阶段。</span>
              {support.reason ? `${support.reason}。` : ''}
              老师和提示的内容由 AI 生成，可能不够地道，仅供参考。
            </p>
          </section>
        )}

        {/* 场景卡片 */}
        {state?.scenario && (
          <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <p className="text-[10px] font-bold text-teal-600">当前场景</p>
            <p className="mt-0.5 text-sm font-bold text-zinc-800 dark:text-zinc-100">
              {state.scenario.title}
            </p>
            <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
              {state.scenario.desc}
            </p>
            {state.scenario.reason && (
              <p className="mt-1.5 rounded-lg bg-teal-50 px-2.5 py-1.5 text-[10px] leading-relaxed text-teal-700 dark:bg-teal-900/30 dark:text-teal-300">
                <span className="font-bold">为什么是这个场景：</span>
                {state.scenario.reason}
              </p>
            )}
            <p className="mt-1.5 text-[10px] text-zinc-400">
              {state.scenario.roles?.teacher} × {state.scenario.roles?.user}
            </p>
          </section>
        )}

        {/* 词汇 */}
        {phase === 'vocab' && state?.vocab?.length > 0 && (
          <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <p className="mb-2 text-[10px] font-bold text-teal-600">先认识几个词</p>
            <div className="space-y-1.5">
              {state.vocab.map((v) => (
                <button
                  key={v.word}
                  type="button"
                  onClick={() => speak(v.word, 'dialect')}
                  className="flex w-full items-center gap-2 rounded-xl bg-zinc-50 px-3 py-2 text-left active:scale-[0.99] dark:bg-zinc-900"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-bold text-zinc-800 dark:text-zinc-100">
                      {v.word}
                    </span>
                    {v.romanization && (
                      <span className="block font-mono text-[10px] text-teal-600">
                        {v.romanization}
                      </span>
                    )}
                    <span className="block text-[11px] text-zinc-500 dark:text-zinc-400">
                      {v.mandarin}
                    </span>
                  </span>
                  <span className="material-icons !text-base text-teal-500">volume_up</span>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* 老师说的话 */}
        {teacherText && (
          <section className="rounded-2xl bg-white p-4 dark:bg-zinc-800">
            <div className="mb-2 flex items-center gap-2.5">
              {/*
                老师形象 —— 必须显示，且必须与方言对应。

                原来这里只有一个小圆点和"老师"两个字，整个对话页看不到人，
                而课堂里是有虚拟人的 —— 学生在两个页面之间切换会明显觉得
                "对话不是在跟这个老师说话"。

                对话是 LLM 实时生成的文本，没有预生成的口型片段，
                所以这里放立绘 + 说话状态；不做假口型（宁可不动，也不做错的口型）。
              */}
              {tutor?.portrait ? (
                <img
                  src={tutor.portrait}
                  alt={tutor.name || '老师'}
                  className={`h-9 w-9 shrink-0 rounded-full object-cover object-top ring-2 transition-all duration-300 ${
                    speaking
                      ? 'scale-105 ring-teal-400'
                      : 'ring-zinc-200 dark:ring-zinc-700'
                  }`}
                />
              ) : (
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    speaking ? 'animate-pulse bg-teal-500' : 'bg-zinc-300'
                  }`}
                />
              )}
              <span className="text-[10px] font-bold text-zinc-500">
                {tutor?.name || '老师'}
                {teacherLang === 'mandarin' ? '（普通话）' : ''}
              </span>
              {speaking && (
                <span className="text-[9px] font-medium text-teal-500">说话中…</span>
              )}
              {/*
                慢放按钮 —— 方言语速偏快时，学生只能反复点重听（同速），
                听多少遍还是那么快。0.7 倍速是目前最直接的补救。
              */}
              <button
                type="button"
                onClick={() => speak(teacherText, teacherLang, 0.7)}
                className="ml-auto rounded-full bg-zinc-100 px-2 py-0.5 text-[9px] font-bold text-zinc-500 active:opacity-60 dark:bg-zinc-700 dark:text-zinc-300"
                aria-label="慢速重听"
              >
                慢一点
              </button>
              <button
                type="button"
                onClick={() => speak(teacherText, teacherLang)}
                className="text-teal-500 active:opacity-60"
                aria-label="重听"
              >
                <span className="material-icons !text-base">replay</span>
              </button>
            </div>
            <p className="text-base font-bold leading-snug text-zinc-800 dark:text-zinc-100">
              {teacherText}
            </p>
            {!streamLine && turn.speakMandarin && (
              <p className="mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                {turn.speakMandarin}
              </p>
            )}
            {/* 台词先到了、提示还在生成 —— 明确告知，别让学生以为卡住 */}
            {streamLine && thinking && (
              <p className="mt-1.5 flex items-center gap-1 text-[10px] text-zinc-400">
                <span className="material-icons animate-spin !text-[12px]">refresh</span>
                提示还在准备…
              </p>
            )}
          </section>
        )}

        {/* 学生刚说的话（识别结果）—— 等待期间要让学生看见自己说了什么 */}
        {thinking && heard && (
          <section className="ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-teal-500 px-3.5 py-2.5 text-white">
            <p className="text-[9px] font-medium opacity-80">你说的</p>
            <p className="text-sm font-medium leading-snug">{heard}</p>
          </section>
        )}

        {/* 思考中（台词还没出来时才有意义）*/}
        {thinking && !streamLine && (
          <section className="flex items-center gap-2.5 rounded-2xl bg-white px-4 py-3 dark:bg-zinc-800">
            <span className="material-icons animate-spin text-teal-500">refresh</span>
            <span className="text-[11px] text-zinc-500">老师在想怎么回你… {elapsed}s</span>
          </section>
        )}

        {/* 水平变化 */}
        {turn?.levelChange?.changed && (
          <section
            className={`rounded-2xl p-3.5 ${
              turn.levelChange.changed === 'up'
                ? 'bg-emerald-50 dark:bg-emerald-900/30'
                : 'bg-sky-50 dark:bg-sky-900/30'
            }`}
          >
            <p className="text-[11px] leading-relaxed text-zinc-700 dark:text-zinc-200">
              {turn.levelChange.changed === 'up' ? '🎉 ' : '💡 '}
              {turn.levelChange.message}
            </p>
          </section>
        )}

        {/*
          话题进度条

          加这个是因为"聊得下去"这件事**必须可见**才让人放心：
          用户反馈过「聊着聊着就聊死了，没话说」。
          光靠老师不冷场还不够 —— 学生得知道"后面还有得聊"，
          才愿意继续接话。顺带它也解释了为什么老师有时候会主动换话题。
        */}
        {turn?.topicProgress && turn.topicProgress.total > 0 && (
          <section className="rounded-2xl bg-sky-50 p-3 dark:bg-sky-900/25">
            <div className="flex items-center gap-2">
              <span className="material-icons !text-sm text-sky-600 dark:text-sky-400">
                forum
              </span>
              <p className="flex-1 text-[11px] text-sky-800 dark:text-sky-200">
                话题 {Math.min(turn.topicProgress.index + 1, turn.topicProgress.total)}/
                {turn.topicProgress.total}
                {turn.topicProgress.current ? ` · 正在聊「${turn.topicProgress.current}」` : ''}
              </p>
              {turn?.topicAction === 'pivot' && (
                <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-bold text-sky-700 dark:text-sky-300">
                  刚换了话题
                </span>
              )}
            </div>
            <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-sky-100 dark:bg-sky-900/50">
              <div
                className="h-full rounded-full bg-sky-500 transition-all"
                style={{
                  width: `${Math.round(
                    ((turn.topicProgress.index + 1) / turn.topicProgress.total) * 100
                  )}%`,
                }}
              />
            </div>
          </section>
        )}

        {/*
          轮次快到上限了 —— 提前打招呼。
          原来到 40 轮直接收尾，学生正聊得投入时会以为程序断了。
        */}
        {isChat && turn?.turnsLeft != null && turn.turnsLeft <= 3 && turn.turnsLeft > 0 && (
          <p className="text-center text-[10px] text-zinc-400">
            再聊 {turn.turnsLeft} 轮我们就收尾啦，想说的是可以现在说。
          </p>
        )}

        {/* 跑题 */}
        {turn?.topicAction === 'redirect' && (
          <section className="rounded-2xl bg-amber-50 p-3.5 dark:bg-amber-900/30">
            <p className="text-[11px] leading-relaxed text-amber-800 dark:text-amber-200">
              老师把话题拉回了当前场景。（不是批评你——跑题很正常，拉回来接着聊就好）
            </p>
          </section>
        )}
        {/*
          ⚠️ 这里原来还有一个 `topicAction === 'warn'` 的分支。
          现已移除：'warn' 是旧策略的产物（"我们先把眼前这个场景聊完好不好？"），
          新策略下没有这一档 —— 连续跑题只会换话题，不会警告学生。
          留着死分支会让后来人以为还有这套逻辑。
        */}

        {/* 逐字对照 */}
        {turn?.comparison?.target && (
          <section className="rounded-2xl bg-white p-3.5 dark:bg-zinc-800">
            <p className="text-[10px] text-zinc-400">你说的和目标句对照</p>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {[...turn.comparison.target]
                .filter((c) => /\S/.test(c))
                .map((c, i) => (
                  <span
                    key={`${c}-${i}`}
                    className={`flex h-7 min-w-7 items-center justify-center rounded-lg px-1.5 text-sm font-bold ${
                      /*
                       * ⚠️ 用服务端回显的 heard，不要用本地的 heard 状态。
                       *
                       * 本地 heard 在新一轮数据到达时会被清空，于是每个字都判成
                       * "没说到"→ 整片红色，学生以为自己一句话全说错了。
                       */
                      (turn.comparison.heard || '').includes(c)
                        ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                        : 'bg-rose-50 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300'
                    }`}
                  >
                    {c}
                  </span>
                ))}
            </div>
            <p className="mt-1.5 text-[10px] leading-relaxed text-zinc-400">
              这不是发音评分，只表示这些字有没有出现在识别结果里。
            </p>
          </section>
        )}

        {/* 结束 */}
        {isWrapup && (
          <section className="rounded-2xl bg-white p-5 dark:bg-zinc-800">
            <div className="text-center">
              <span className="material-icons text-3xl text-teal-500">check_circle</span>
              <p className="mt-2 text-sm font-bold text-zinc-700 dark:text-zinc-200">
                这次对话到这里
              </p>
            </div>

            {/*
              结束总结 —— 原来这里只有一句"下次接着练"，
              学生聊了十几轮却不知道自己练到了什么。
              数据全部来自会话状态（不额外调 LLM），所以是即时的、也不会编。
            */}
            {turn?.summary && (
              <div className="mt-4 space-y-3.5">
                <div className="grid grid-cols-3 gap-2 rounded-xl bg-zinc-50 py-3 dark:bg-zinc-900/40">
                  <div className="text-center">
                    <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">
                      {turn.summary.spokenTurns}
                    </p>
                    <p className="text-[10px] text-zinc-400">开口轮数</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">
                      {turn.summary.topicsDone}
                      <span className="text-xs text-zinc-400">
                        /{turn.summary.agendaTotal || '-'}
                      </span>
                    </p>
                    <p className="text-[10px] text-zinc-400">聊过话题</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-zinc-800 dark:text-zinc-100">
                      {Math.round((turn.summary.matchRatio || 0) * 100)}%
                    </p>
                    <p className="text-[10px] text-zinc-400">说到点上</p>
                  </div>
                </div>

                {turn.summary.vocab?.length > 0 && (
                  <div>
                    <p className="text-[10px] text-zinc-400">今天用到的词</p>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {turn.summary.vocab.map((w) => (
                        <span
                          key={w}
                          className="rounded-lg bg-teal-50 px-2 py-1 text-xs font-medium text-teal-700 dark:bg-teal-900/40 dark:text-teal-300"
                        >
                          {w}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  {(turn.lines || []).map((l) => (
                    <p
                      key={l}
                      className="text-[11px] leading-relaxed text-zinc-600 dark:text-zinc-300"
                    >
                      {l}
                    </p>
                  ))}
                </div>

                <p className="text-[9px] leading-relaxed text-zinc-400">
                  「说到点上」只看用字有没有对上，不是发音评分。
                </p>
              </div>
            )}

            <div className="mt-4 text-center">
              <Link
                to="/courses"
                className="inline-block rounded-full bg-teal-500 px-4 py-2 text-xs font-bold text-white"
              >
                回去上课
              </Link>
            </div>
          </section>
        )}
      </main>

      {/* 底部操作区（在 flex 流里，根容器的 paddingBottom 保证不被导航压住） */}
      {!isWrapup && (
        <footer className="shrink-0 space-y-2.5 border-t border-zinc-200 bg-white px-5 pt-3 dark:border-zinc-700 dark:bg-zinc-900">
          {/* 提示栏 */}
          {isChat && turn?.hints?.length > 0 && (
            <div className="overflow-hidden rounded-2xl bg-zinc-50 dark:bg-zinc-800">
              <button
                type="button"
                onClick={toggleHint}
                className="flex w-full items-center gap-2 px-3.5 py-2.5"
              >
                <span className="material-icons !text-base text-teal-500">
                  {hintOpen ? 'expand_less' : 'lightbulb_outline'}
                </span>
                <span className="flex-1 text-left text-[11px] font-bold text-zinc-600 dark:text-zinc-300">
                  {hintOpen ? '收起提示' : '不知道怎么答？看看提示'}
                </span>
                {prefetching && <span className="text-[9px] text-zinc-400">准备语音中…</span>}
                <span className="material-icons !text-base text-zinc-400">
                  {hintOpen ? 'expand_less' : 'expand_more'}
                </span>
              </button>

              {hintOpen && (
                <div className="space-y-1.5 px-3 pb-3">
                  {turn.hints.map((h) => (
                    <button
                      key={h.text}
                      type="button"
                      onClick={() => playHint(h)}
                      className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left transition ${
                        chosenHint?.text === h.text
                          ? 'bg-teal-500/15'
                          : 'bg-white active:scale-[0.99] dark:bg-zinc-900'
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold text-zinc-800 dark:text-zinc-100">
                          {h.text}
                        </span>
                        {h.romanization && (
                          <span className="block font-mono text-[10px] text-teal-600">
                            {h.romanization}
                          </span>
                        )}
                        <span className="block text-[10px] text-zinc-500 dark:text-zinc-400">
                          {h.mandarin}
                        </span>
                      </span>
                      <span className="material-icons !text-base text-teal-500">volume_up</span>
                    </button>
                  ))}
                  <p className="pt-0.5 text-[9px] leading-relaxed text-zinc-400">
                    照着念就行。连续几轮不用看提示也能答上来，老师会让你自由回答。
                  </p>
                </div>
              )}
            </div>
          )}

          {/* 对话阶段：录音 */}
          {isChat && !busy && speechBar('点麦克风，用方言回答老师')}

          {/*
            兴趣阶段：**默认用说的**，打字是备选。

            原来这里只有一个输入框。但那正是"老师想了解你的兴趣"的环节 ——
            让学生在这里打字，等于在学口语的入口处先要求写字。
          */}
          {isInterest && !busy && (
            <div className="space-y-2.5">
              {speechBar('点麦克风，说说你平时喜欢做什么（普通话就行）')}

              {!textMode ? (
                <button
                  type="button"
                  onClick={() => setTextMode(true)}
                  className="w-full text-center text-[10px] text-zinc-400 active:opacity-60"
                >
                  不方便说话？点这里打字
                </button>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const v = draft.trim();
                    if (v) {
                      setDraft('');
                      advance({ userText: v });
                    }
                  }}
                  className="flex gap-2"
                >
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    autoFocus
                    placeholder="说说你平时喜欢做什么…"
                    className="min-w-0 flex-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5 text-sm text-zinc-800 outline-none focus:border-teal-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                  />
                  <button
                    type="submit"
                    disabled={!draft.trim()}
                    className="rounded-xl bg-teal-500 px-4 text-xs font-bold text-white disabled:opacity-40"
                  >
                    发送
                  </button>
                </form>
              )}
            </div>
          )}

          {/*
            兴趣阶段：**只放输入框，不放按钮**。
            原来这里有个禁用的「先说点什么吧」按钮，纯属多余且误导
            —— 用户点了没反应，真正的输入框在它下面。
          */}
          {isInterest && !busy && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const v = draft.trim();
                if (v) {
                  setDraft('');
                  advance({ userText: v });
                }
              }}
              className="flex gap-2"
            >
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="说说你平时喜欢做什么…"
                className="min-w-0 flex-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3.5 py-2.5 text-sm text-zinc-800 outline-none focus:border-teal-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
              />
              <button
                type="submit"
                disabled={!draft.trim()}
                className="rounded-xl bg-teal-500 px-4 text-xs font-bold text-white disabled:opacity-40"
              >
                发送
              </button>
            </form>
          )}

          {/* 场景/词汇阶段：继续 */}
          {!isChat && !isInterest && !busy && (
            <button
              type="button"
              onClick={() => advance({ userText: '好了，开始吧' })}
              className="w-full rounded-2xl bg-teal-500 py-3.5 text-sm font-bold text-white"
            >
              开始对话
            </button>
          )}

          {error && (
            <p className="rounded-xl bg-rose-50 px-3 py-2 text-[11px] text-rose-600 dark:bg-rose-900/30 dark:text-rose-300">
              {error}
            </p>
          )}

          <p className="pb-1 text-center text-[9px] leading-relaxed text-zinc-400">
            对话由 AI 生成，未经母语者验证，仅供练习参考
          </p>
        </footer>
      )}

      <BottomNav />
    </Shell>
  );
}

/**
 * 页面外壳
 *
 * 关键：`h-screen` + `paddingBottom = 导航高度`。
 * 这样 flex 列（内容区 + 输入区）的底部停在 fixed 导航上方，
 * 输入区不会被导航盖住 —— 这是之前"导航挡住按钮"的根因。
 */
function Shell({ children }) {
  return (
    <div
      className="flex h-screen flex-col bg-[#fdfaf1] dark:bg-zinc-900"
      style={{ paddingBottom: NAV_HEIGHT }}
    >
      {children}
    </div>
  );
}
